import React, {createContext, useCallback, useContext, useEffect, useReducer, useState} from 'react';
import {
	Backdrop, CircularProgress,
	Container, Fab,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AddIcCallTwoToneIcon from '@mui/icons-material/AddIcCallTwoTone';
import {FormProvider, useForm} from "react-hook-form";
import {FAQ_INITIAL_STATE, FaqReducer, initFaqState} from "../../../reducers/FaqReducer";
import {searchFaq} from "../../../services/FaqServices";
import {cleanSearchedTickets, generateTicket, searchTicket,} from "../../../services/TicketServices";
import {checkProperties, clean, isEmpty} from "../../../utils/GenericUtils";
import {AppContext, TicketContext} from "../../../App";
import {setOpenSnackbar} from "../../../services/AppServices";
import DoneOutlineTwoToneIcon from '@mui/icons-material/DoneOutlineTwoTone';
import AnagraficaCard from "./form/AnagraficaCard";
import {BaseStyle} from "../../../styles/shared/BaseStyle";
import StoricoCard from "./storico/StoricoCard";
import DettaglioCard from "./form/DettaglioCard";
import AssociatoCard from "./form/AssociatoCard";
import {getSearchData, getTicketData} from "../../../services/utils/TicketApiData";
import clsx from "clsx";
import HideAppBar from "../../widgets/header/HideAppBar";
import {useHistory} from "react-router-dom";
import {green, orange} from "@mui/material/colors";

export const FaqContext = createContext();

export const CallcenterTicketForm = () => {
	const classes = BaseStyle();
	const [faqState, faqDispatch] = useReducer(FaqReducer, FAQ_INITIAL_STATE, initFaqState);
	const appContext = useContext(AppContext);
	const ticketContext = useContext(TicketContext);
	const methods = useForm({mode: "onChange"})
	const [submit, setSubmit] = useState(false);
	const [status, setStatus] = useState("");
	const [associati, setAssociati] = useState([]);
	const [tags, setTags] = useState([]);
	let history = useHistory();

	const onSolvedSumbit = useCallback((data) => {
		setSubmit(true);
		setStatus("Risolto");
		let call = data;
		if (data.tipoDocumento && data.tipoDocumento === "Utente anonimo*") {
			call = {
				...data,
				codF: "UTENTE_ANONIMO"
			}
		}
		generateTicket(getTicketData(call, "Risolto", associati, tags), ticketContext.dispatch)
	}, [ticketContext.dispatch, associati, tags])

	const onSubmit = useCallback((data) => {
		setStatus("Nuovo");
		if (data.tipoDocumento && data.tipoDocumento === "Utente anonimo*") {
			setOpenSnackbar("warning", "Se registri una chiamata non puoi inserire l'utente anonimo. É necessario avere dei dati per poter gestire il ticket!", appContext.dispatch);
		} else {
			setSubmit(true);
			generateTicket(getTicketData(data, "Nuovo", associati, tags), ticketContext.dispatch)
		}
	}, [ticketContext.dispatch, associati, tags, appContext.dispatch])

	const handleKeyDown = useCallback((event) => {
		if (event.ctrlKey && event.keyCode === 13) {
			methods.trigger().then(res => {
				if (res) {
					onSubmit(methods.getValues())
				}
			});
		}
	}, [methods, onSubmit])

	const onTicketSearch = useCallback(() => {
		let data = methods.getValues();
		let anagraficaToSearch = {};
		if (data.nome) {
			anagraficaToSearch = {
				...anagraficaToSearch,
				nome: data.nome
			}
		}
		if (data.cognome) {
			anagraficaToSearch = {
				...anagraficaToSearch,
				cognome: data.cognome
			}
		}
		if (data.email) {
			anagraficaToSearch = {
				...anagraficaToSearch,
				email: data.email
			}
		}
		if (data.cell) {
			anagraficaToSearch = {
				...anagraficaToSearch,
				cell: data.cell
			}
		}
		if (data.cell2) {
			anagraficaToSearch = {
				...anagraficaToSearch,
				cell2: data.cell2
			}
		}
		data = {
			...data,
			documento: data.codF ? data.codF?.toUpperCase() :
				data.cartaIdentita ? data.cartaIdentita?.toUpperCase() :
					data.patente ? data.patente?.toUpperCase() :
						data.passaporto ? data.passaporto?.toUpperCase() :
							data.codiceSTP ? data.codiceSTP?.toUpperCase() : "",
		}
		/**
		 * Rimuovo le note per la ricerca
		 */
		delete data["dettaglioRichiesta"];
		delete data["tipoDocumento"];
		if (!isEmpty(anagraficaToSearch)) {
			data = {
				...data,
				anagraficaToSearch: anagraficaToSearch,
			}
		} else {
			data = {
				...data,
				anagraficaToSearch: null
			}
		}
		if (checkProperties(data)) {
			searchTicket({
				...clean(getSearchData(data)
				), active: null,
				sendMail: null,
				contattoMail: null
			}, ticketContext.dispatch);
		} else {
			cleanSearchedTickets(ticketContext.dispatch);
		}
	}, [methods, ticketContext.dispatch]);

	const aggiungiPaziente = useCallback(() => {
		setAssociati(prevState => [...prevState, prevState.length]);
	}, [])

	const rimuoviPaziente = useCallback((elem) => {
		setAssociati(prevState => prevState.filter(item => item !== elem));
	}, [])

	useEffect(() => {
		searchFaq({}, faqDispatch);
	}, [faqDispatch]);

	useEffect(() => {
		cleanSearchedTickets(ticketContext.dispatch);
	}, [ticketContext.dispatch])

	useEffect(() => {
		if (!ticketContext.state.loadingSearchTicket && ticketContext.state.errorSearchTicket !== "") {
			setOpenSnackbar("error", ticketContext.state.errorSearchTicket, appContext.dispatch);
		}
	}, [appContext.dispatch, ticketContext.state.loadingSearchTicket, ticketContext.state.errorSearchTicket])

	useEffect(() => {
		if (!ticketContext.state.loadingAddTicket && ticketContext.state.errorAddTicket === "" && status !== "") {
			let message = status === "Nuovo" ? "Ticket inoltrato e registrato con successo!" : "Ticket risolto registrato con successo!";
			setOpenSnackbar("success", message, appContext.dispatch);
			history.push("/gestione/callcenter");
			setTags([]);
			setSubmit(false);
			window.location.reload();
		} else if (!ticketContext.state.loadingAddTicket && ticketContext.state.errorAddTicket !== "") {
			setOpenSnackbar("error", ticketContext.state.errorAddTicket, appContext.dispatch);
			setSubmit(false);
		}
	}, [appContext.dispatch, ticketContext.state.loadingAddTicket, ticketContext.state.errorAddTicket, status, history])

	useEffect(() => {
		document.body.style = "background: none";
	}, []);

	return (
		<React.Fragment>
			<FaqContext.Provider value={{state: faqState, dispatch: faqDispatch}}>
				<Backdrop open={submit} className={classes.backdrop}>
					<CircularProgress color={"primary"}/>
				</Backdrop>
				<main className={classes.main}>
					<HideAppBar title={"Callcenter"}/>
					<Container maxWidth={"xl"} component={"article"} style={{paddingTop: 12}}>
						<FormProvider {...methods}>
							<Grid container spacing={3}>
								<Grid item xs={12} md={7}>
									<AnagraficaCard onSearch={onTicketSearch} aggiungiPaziente={aggiungiPaziente}/>
								</Grid>

								<Grid item xs={12} md={5}>
									<StoricoCard showButton={true} isCcm={true} firstLevel={true}
									             style={{
										             flexGrow: 1,
										             height: 591.64,
										             overflow: "scroll",
													 scrollbarWidth: "none"
									             }}/>
								</Grid>

								{
									associati.length > 0 && associati.map((item) => {
										return (<Grid item xs={12} key={item}>
											<AssociatoCard name={item} rimuoviPaziente={rimuoviPaziente}/>
										</Grid>)
									})
								}

								<Grid item xs={12} style={{marginBottom: 20}}>
									<DettaglioCard handleKeyDown={handleKeyDown} tags={tags} setTags={setTags}/>
								</Grid>
							</Grid>

						</FormProvider>
					</Container>
				</main>
				<div className={classes.fabContainer}>
					<Fab
						variant={"extended"}
						type={"submit"}
						style={{
							backgroundColor: orange[700],
							color: "#FFF"
						}}
						className={clsx(classes.fab, classes.amberFab)}
						onClick={methods.handleSubmit(onSubmit)}
						disabled={submit}
					>
						<AddIcCallTwoToneIcon className={classes.extendedIconFab}/>
						<b>Registra chiamata</b>
					</Fab>
					<Fab
						variant={"extended"}
						type={"submit"}
						style={{
							backgroundColor: green[700]
						}}
						className={clsx(classes.fab, classes.greenFab)}
						onClick={methods.handleSubmit(onSolvedSumbit)}
						disabled={submit}
					>
						<DoneOutlineTwoToneIcon className={classes.extendedIconFab}/>
						<b>Chiamata risolta</b>
					</Fab>
				</div>
			</FaqContext.Provider>
		</React.Fragment>
	);
};
